'use client';

import { getCachedSession } from '@/features/auth/utils/sessionCache';
import { ChatListItem, SocketMessage } from '../types/api';
import { ChatContact, ChatMessage } from '../types/chat';

export class ChatService {
  // Convert socket message to local format
  static convertSocketMessageToLocal(socketMessage: SocketMessage, currentUserId: number): ChatMessage {
    const senderId = typeof socketMessage.senderId === 'string'
      ? parseInt(socketMessage.senderId, 10)
      : socketMessage.senderId;

    return {
      id: socketMessage.id,
      senderId,
      senderName: `${socketMessage.sender.companyName ? `${socketMessage.sender.companyName} _ ` : ''}${socketMessage.sender.firstName} ${socketMessage.sender.lastName}`,
      message: socketMessage.content,
      timestamp: new Date(socketMessage.createdAt),
      isOwn: senderId === currentUserId,
      isRead: socketMessage.isRead || false,
      status: 'sent',
      type: socketMessage.type,
      productData: socketMessage.productData,
    };
  }

  // Convert server contacts data to local format
  static async convertContactsFromServer(contactsData: ChatListItem[]): Promise<{
    contacts: ChatContact[];
    messages: Record<number, ChatMessage[]>;
    chatHistory: Record<number, { page: number; hasMore: boolean; total: number; isLoadingMore: boolean }>;
  }> {
    const contacts: ChatContact[] = [];
    const messages: Record<number, ChatMessage[]> = {};
    const chatHistory: Record<number, { page: number; hasMore: boolean; total: number; isLoadingMore: boolean }> = {};

    const session = await getCachedSession();
    const currentUserId = session?.user?.id ? parseInt(session.user.id) : 0;

    contactsData.forEach((data) => {
      if (!data.chatRoom || !data.otherParticipant) return;

      const contact: ChatContact = {
        id: data.chatRoom.id,
        name: `${data.otherParticipant.companyName ? `${data.otherParticipant.companyName}_` : ''}${data.otherParticipant.firstName}${data.otherParticipant.lastName}`,
        username: data.otherParticipant.username,
        lastMessage: data.lastMessage?.content,
        lastMessageTime: data.lastMessage ? new Date(data.lastMessage.createdAt) : undefined,
        unreadCount: data.unreadCount,
      };
      contacts.push(contact);

      if (data.previewMessages && data.previewMessages.length > 0) {
        messages[data.chatRoom.id] = data.previewMessages.map((msg: SocketMessage) => {
          const convertedMsg = this.convertSocketMessageToLocal(msg, currentUserId);
          return convertedMsg;
        });
      }

      if (data.hasMore) {
        chatHistory[data.chatRoom.id] = {
          page: 1,
          hasMore: data.hasMore,
          total: data.previewMessages?.length || 0,
          isLoadingMore: false,
        };
      }
    });

    return { contacts, messages, chatHistory };
  }

  // Generate temporary message ID
  static generateTempId(): string {
    return `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Create optimistic message for immediate UI update
  static async createOptimisticMessage(
    message: string,
    currentUserId?: number,
    currentUsername?: string
  ): Promise<ChatMessage> {
    const session = await getCachedSession();
    const userId = currentUserId || (session?.user?.id ? parseInt(session.user.id) : 0);
    const username = currentUsername || session?.user?.name || 'Unknown';

    return {
      id: -1,
      senderId: userId,
      senderName: username,
      message,
      timestamp: new Date(),
      isOwn: true,
      isRead: false,
      status: 'pending',
      tempId: this.generateTempId(),
    };
  }

  // Find pending message for replacement
  static findPendingMessage(
    messages: ChatMessage[],
    newMessage: ChatMessage
  ): ChatMessage | undefined {
    return messages
      .filter(msg => msg.status === 'pending' || msg.status === 'sent')
      .find(msg =>
        msg.message === newMessage.message &&
        msg.isOwn &&
        msg.tempId
      );
  }

  // Filter unique messages to prevent duplicates
  static filterUniqueMessages(
    newMessages: ChatMessage[],
    existingMessages: ChatMessage[]
  ): ChatMessage[] {
    return newMessages.filter(
      newMsg => !existingMessages.some(existing => existing.id === newMsg.id)
    );
  }

  // Check if user should be notified for new message
  static shouldNotifyUser(activeContactId: number | null, chatRoomId: number): boolean {
    return activeContactId !== chatRoomId;
  }

  // Calculate unread message IDs
  static getUnreadMessageIds(messages: ChatMessage[]): number[] {
    return messages
      .filter(msg => !msg.isOwn && !msg.isRead)
      .map(msg => msg.id);
  }
}